import { create, type StoreApi } from "zustand";
import { shallow } from "zustand/shallow";
import type { EnvironmentFileChildrenDto, EnvironmentFileDto, EnvironmentStatus } from "@/lib/api";
import { newRequestId } from "@/lib/id-generator";
import {
	CHAT_EFFORTS,
	SUBAGENT_PROFILES,
	type ContextUsage,
	type ChatDeltaDto,
	type ChatDeltaKind,
	type ChatIndicator,
	type ChatListEntry,
	type ChatEffort,
	type ChatMessageDto,
	type ChatMetadata,
	type ChatMonitor,
	type ChatSubagentRun,
	type ChatWorkflowRun,
	type PermissionMode,
	type QueuedChatMessageDto,
	type SubagentModelSelection,
	type SubagentRunStatus,
	type SubagentProfile,
	type WorkflowRunStatus,
} from "@/lib/chat";
import { isCompactingNotice } from "@/lib/chat";
import type { HITLState } from "@/components/hitl/hitl";
import { parseStoredChatListFilters, type ChatListFilter } from "@/lib/chat-list-filters";

/**
 * 送信待ちメッセージ配列を「id で一意化 + Seq 昇順」に正規化する。
 * 並行 POST の応答順や初期ロードと SSE の交錯があっても表示順を Seq に固定するため、
 * queuedMessages を書き換える全経路をこのヘルパに通す。
 */
function normalizeQueued(list: readonly QueuedChatMessageDto[]): QueuedChatMessageDto[] {
	const byId = new Map<string, QueuedChatMessageDto>();
	for (const entry of list) byId.set(entry.id, entry);
	return [...byId.values()].sort((a, b) => a.seq - b.seq);
}

/**
 * ポーリングで取り直した一覧のうち、中身が変わっていない要素は前回の参照を使い回す。
 * 全要素が同じなら前回の配列そのものを返し、購読しているチャット履歴全体の再描画を避ける。
 * 要素のフィールドがプリミティブだけであることを前提に浅く比べる。
 */
function reuseUnchanged<T extends { id: string }>(previous: T[] | null, next: T[]): T[] {
	const previousById = new Map(previous?.map((item) => [item.id, item]));
	const merged = next.map((item) => {
		const old = previousById.get(item.id);
		return old !== undefined && shallow(old, item) ? old : item;
	});
	const unchanged =
		previous !== null &&
		previous.length === merged.length &&
		merged.every((item, i) => item === previous[i]);
	return unchanged ? previous : merged;
}

/** previewFileId が fileId を指していれば解除する。プレビュータブを閉じる/dirty 化する各アクションで使う。 */
function clearedPreviewFileId(previewFileId: string | null, fileId: string): string | null {
	return previewFileId === fileId ? null : previewFileId;
}

/** ids から fileId を除いた上で index の位置に挿入し直す。move/pin/unpin で共有する。 */
function insertAt(ids: string[], fileId: string, index: number): string[] {
	return [...ids.slice(0, index), fileId, ...ids.slice(index)];
}

/** fileId をグループのピン群/通常群の境界へ移動する (pin=true でピン群の末尾、false で通常群の先頭)。 */
function setFilePinned(
	groups: readonly EditorGroup[],
	group: EditorGroup,
	fileId: string,
	pin: boolean,
): EditorGroup[] {
	const without = group.fileIds.filter((id) => id !== fileId);
	const pinnedCount = group.pinnedCount + (pin ? 1 : -1);
	const fileIds = insertAt(without, fileId, pin ? group.pinnedCount : pinnedCount);
	return groups.map((g) => (g.id === group.id ? { ...g, fileIds, pinnedCount } : g));
}

export type ConnectionStatus = "streaming" | "idle" | "stopping";

export interface FileRevealSpan {
	start: number;
	end: number;
}

/** ファイルエディタで選択・表示する範囲 (Monaco の IRange と同じ形)。 */
export interface FileRevealRange {
	startLineNumber: number;
	startColumn: number;
	endLineNumber: number;
	endColumn: number;
}

/**
 * ファイルエディタへの行ジャンプ要求。`query`/`caseSensitive` を渡すと、クリックした一致箇所
 * だけでなくファイル内の同じ語句全てをハイライトする (Cmd+F の「すべて強調」相当)。
 * `range` を渡すと、行だけでなくその範囲を選択して表示する (Problems パネルの診断ジャンプ用)。
 */
export interface FileRevealRequest {
	line: number;
	range?: FileRevealRange;
	spans?: readonly FileRevealSpan[];
	query?: string;
	caseSensitive?: boolean;
}

export type EditorSplitDirection = "left" | "right" | "up" | "down";

/** 分割エディタの 1 ペイン。ファイルは常にちょうど 1 つのグループに属する。 */
export interface EditorGroup {
	id: string;
	fileIds: string[];
	/**
	 * このグループでピン留めされたファイルタブの数。invariant: `fileIds` の先頭 pinnedCount 個が
	 * ピン留め済み (0 <= pinnedCount <= fileIds.length)。
	 */
	pinnedCount: number;
	activeFileId: string | null;
	/** このグループでプレビュー表示中のファイル。invariant: null または fileIds に含まれる。 */
	previewFileId: string | null;
}

/** エディタグループの入れ子分割構成。leaf がグループ、内部ノードが左右/上下分割を表す。 */
export type EditorLayoutNode =
	| { kind: "group"; groupId: string }
	| {
			kind: "split";
			direction: "horizontal" | "vertical";
			first: EditorLayoutNode;
			second: EditorLayoutNode;
	  };

export const INITIAL_EDITOR_GROUP_ID = "editor-1";
let editorGroupSequence = 1;

function initialEditorGroups(): EditorGroup[] {
	return [
		{
			id: INITIAL_EDITOR_GROUP_ID,
			fileIds: [],
			pinnedCount: 0,
			activeFileId: null,
			previewFileId: null,
		},
	];
}

function initialEditorLayout(): EditorLayoutNode {
	return { kind: "group", groupId: INITIAL_EDITOR_GROUP_ID };
}

function allOpenFileIds(groups: readonly EditorGroup[]): string[] {
	return groups.flatMap((group) => group.fileIds);
}

/** fileId を含むエディタグループを探す。 */
export function findGroupWithFile(
	groups: readonly EditorGroup[],
	fileId: string,
): EditorGroup | undefined {
	return groups.find((group) => group.fileIds.includes(fileId));
}

function activeEditorGroup(
	groups: readonly EditorGroup[],
	activeGroupId: string,
): EditorGroup | undefined {
	return groups.find((group) => group.id === activeGroupId);
}

/**
 * ファイルを (プレビューでなく) 開く: 既に (どのグループかを問わず) 開いていればそのグループへ
 * フォーカスを移し、プレビュータブだった場合はピン留めに昇格する。未オープンならアクティブグループへ
 * 追加する。openFile/openFileAtLine で共有する。
 */
function resolveOpenFileGroups(
	editorGroups: EditorGroup[],
	activeEditorGroupId: string,
	fileId: string,
): {
	editorGroups: EditorGroup[];
	activeEditorGroupId: string;
	activeFileId: string;
	openFileIds?: string[];
	previewFileId?: string | null;
} {
	const existingGroup = findGroupWithFile(editorGroups, fileId);
	if (existingGroup) {
		const previewFileId = clearedPreviewFileId(existingGroup.previewFileId, fileId);
		const groups = editorGroups.map((group) =>
			group.id === existingGroup.id ? { ...group, activeFileId: fileId, previewFileId } : group,
		);
		return {
			editorGroups: groups,
			activeEditorGroupId: existingGroup.id,
			activeFileId: fileId,
			previewFileId,
		};
	}
	const groups = editorGroups.map((group) =>
		group.id === activeEditorGroupId
			? { ...group, fileIds: [...group.fileIds, fileId], activeFileId: fileId }
			: group,
	);
	return {
		editorGroups: groups,
		activeEditorGroupId,
		activeFileId: fileId,
		openFileIds: allOpenFileIds(groups),
	};
}

/**
 * fileId がプレビュー (transient) タブなら、常設タブへ昇格させた状態の差分を返す。
 * 対象がプレビュータブでなければ null を返す。
 */
function promotedFromPreview(
	editorGroups: EditorGroup[],
	activeEditorGroupId: string,
	previewFileId: string | null,
	fileId: string,
): { editorGroups: EditorGroup[]; previewFileId: string | null } | null {
	const targetGroup = findGroupWithFile(editorGroups, fileId);
	if (!targetGroup || targetGroup.previewFileId !== fileId) return null;
	return {
		editorGroups: editorGroups.map((group) =>
			group.id === targetGroup.id ? { ...group, previewFileId: null } : group,
		),
		previewFileId: activeEditorGroupId === targetGroup.id ? null : previewFileId,
	};
}

/** グループから fileId を取り除いた状態を返す。取り除いた対象がアクティブ/プレビュー中だった場合は近傍タブへ移す。 */
function removeFileFromGroup(group: EditorGroup, fileId: string): EditorGroup {
	const index = group.fileIds.indexOf(fileId);
	const fileIds = group.fileIds.filter((id) => id !== fileId);
	const activeFileId =
		group.activeFileId === fileId
			? (fileIds[Math.min(index, fileIds.length - 1)] ?? null)
			: group.activeFileId;
	const pinnedCount =
		index !== -1 && index < group.pinnedCount ? group.pinnedCount - 1 : group.pinnedCount;
	return {
		...group,
		fileIds,
		pinnedCount,
		activeFileId,
		previewFileId: clearedPreviewFileId(group.previewFileId, fileId),
	};
}

/**
 * セグメンテーションをファイルタブ帯に並べていた頃のタブ id の接頭辞。保存済みレイアウトに
 * 残っているため、読み戻し時に取り除く。
 */
const LEGACY_PANEL_TAB_PREFIX = "panel:";

/** グループから旧セグメンテーションタブを取り除く。保存済みレイアウトの読み戻しで使う。 */
function withoutLegacyPanelTabs(group: EditorGroup): EditorGroup {
	return group.fileIds.reduce(
		(current, id) =>
			id.startsWith(LEGACY_PANEL_TAB_PREFIX) ? removeFileFromGroup(current, id) : current,
		group,
	);
}

/** ファイルタブを 1 つ閉じた後の状態差分を返す。 */
function closedTabState(state: ChatState, tabId: string) {
	const targetGroup = findGroupWithFile(state.editorGroups, tabId);
	if (!targetGroup) return state;
	const updatedGroup = removeFileFromGroup(targetGroup, tabId);
	const editorGroups = state.editorGroups.map((group) =>
		group.id === targetGroup.id ? updatedGroup : group,
	);
	const activeGroup = activeEditorGroup(editorGroups, state.activeEditorGroupId);
	return {
		editorGroups,
		openFileIds: allOpenFileIds(editorGroups),
		dirtyFileIds: state.dirtyFileIds.includes(tabId)
			? state.dirtyFileIds.filter((id) => id !== tabId)
			: state.dirtyFileIds,
		activeFileId: activeGroup?.activeFileId ?? null,
		previewFileId: activeGroup?.previewFileId ?? null,
	};
}

/** 指定グループを direction 側で分割し、新しい空グループを挿入したレイアウトを返す。 */
function splitEditorLayout(
	node: EditorLayoutNode,
	groupId: string,
	newGroupId: string,
	direction: EditorSplitDirection,
): EditorLayoutNode {
	if (node.kind === "group") {
		if (node.groupId !== groupId) return node;
		const newGroup: EditorLayoutNode = { kind: "group", groupId: newGroupId };
		const currentGroup: EditorLayoutNode = { kind: "group", groupId };
		const isBefore = direction === "left" || direction === "up";
		return {
			kind: "split",
			direction: direction === "left" || direction === "right" ? "horizontal" : "vertical",
			first: isBefore ? newGroup : currentGroup,
			second: isBefore ? currentGroup : newGroup,
		};
	}
	const first = splitEditorLayout(node.first, groupId, newGroupId, direction);
	if (first !== node.first) return { ...node, first };
	const second = splitEditorLayout(node.second, groupId, newGroupId, direction);
	return second === node.second ? node : { ...node, second };
}

/** レイアウト木から指定グループの leaf を取り除き、親の分割ノードをもう一方の枝に潰す。 */
function removeGroupFromLayout(node: EditorLayoutNode, groupId: string): EditorLayoutNode {
	if (node.kind === "group") return node;
	if (node.first.kind === "group" && node.first.groupId === groupId) return node.second;
	if (node.second.kind === "group" && node.second.groupId === groupId) return node.first;
	const first = removeGroupFromLayout(node.first, groupId);
	if (first !== node.first) return { ...node, first };
	const second = removeGroupFromLayout(node.second, groupId);
	return second === node.second ? node : { ...node, second };
}

/**
 * fileId をソースグループから取り除いた結果と、それによってグループが空になった場合の groupId
 * (空にならなければ null) をまとめて返す。moveFileToGroup/splitEditorWithFile で共有する。
 */
function detachFileFromGroup(
	sourceGroup: EditorGroup,
	fileId: string,
): { updatedSourceGroup: EditorGroup; emptiedGroupId: string | null } {
	const updatedSourceGroup = removeFileFromGroup(sourceGroup, fileId);
	return {
		updatedSourceGroup,
		emptiedGroupId: updatedSourceGroup.fileIds.length === 0 ? updatedSourceGroup.id : null,
	};
}

/**
 * ドラッグでファイルを移動した後、空になった移動元グループ (emptiedGroupId) があれば取り除いて
 * 分割を閉じる。呼び出し側は、取り除いても必ず 1 つ以上グループが残ることを保証すること。
 */
function closeIfEmptied(
	groups: EditorGroup[],
	layout: EditorLayoutNode,
	emptiedGroupId: string | null,
): { editorGroups: EditorGroup[]; editorLayout: EditorLayoutNode } {
	if (emptiedGroupId === null) return { editorGroups: groups, editorLayout: layout };
	return {
		editorGroups: groups.filter((group) => group.id !== emptiedGroupId),
		editorLayout: removeGroupFromLayout(layout, emptiedGroupId),
	};
}

/**
 * 環境が切り替わってファイル一覧を破棄するときに、開いていたタブとエディタ分割を初期状態へ戻す。
 * openFileIds だけを空にするとグループ側に前の環境のタブが残るため、分割構成ごと初期化する。
 */
function clearedEditorState() {
	return {
		openFileIds: [],
		activeFileId: null,
		previewFileId: null,
		dirtyFileIds: [],
		editorGroups: initialEditorGroups(),
		editorLayout: initialEditorLayout(),
		activeEditorGroupId: INITIAL_EDITOR_GROUP_ID,
	};
}

/**
 * リフレッシュ後もツリー上の選択を維持してよいかを判定し、維持できないときは null を返す。
 * 親ディレクトリが未読込のときは消えたと断定できないため、選択をそのまま残す。
 */
function selectedNodeAfterRefresh(
	children: ReadonlyMap<string, EnvironmentFileChildrenDto>,
	selectedPath: string | null,
): string | null {
	if (selectedPath === null) return null;
	const slash = selectedPath.lastIndexOf("/");
	const parent = slash === -1 ? "" : selectedPath.slice(0, slash);
	const loadedParent = children.get(parent);
	if (!loadedParent) return selectedPath;
	return loadedParent.entries.some((entry) => entry.path === selectedPath) ? selectedPath : null;
}

/**
 * 索引から消えたファイルのタブを閉じた後のエディタ状態を返す。開いたままのタブしか
 * 残さないので、閉じた分だけアクティブ・プレビュー・未保存マークも整理する。
 */
function closedMissingTabState(state: ChatState, keepTab: (fileId: string) => boolean) {
	let groupsChanged = false;
	const editorGroups = state.editorGroups.map((group) => {
		const fileIds = group.fileIds.filter(keepTab);
		if (fileIds.length === group.fileIds.length) return group;
		groupsChanged = true;
		const oldIndex = group.activeFileId ? group.fileIds.indexOf(group.activeFileId) : -1;
		const pinnedCount = group.fileIds.slice(0, group.pinnedCount).filter(keepTab).length;
		return {
			...group,
			fileIds,
			pinnedCount,
			activeFileId:
				group.activeFileId && fileIds.includes(group.activeFileId)
					? group.activeFileId
					: (fileIds[Math.min(Math.max(oldIndex, 0), fileIds.length - 1)] ?? null),
			previewFileId:
				group.previewFileId && fileIds.includes(group.previewFileId) ? group.previewFileId : null,
		};
	});
	const nextGroups = groupsChanged ? editorGroups : state.editorGroups;
	const activeGroup = activeEditorGroup(nextGroups, state.activeEditorGroupId) ?? nextGroups[0];
	const dirtyFileIds = state.dirtyFileIds.filter(keepTab);

	return {
		editorGroups: nextGroups,
		openFileIds: groupsChanged ? allOpenFileIds(nextGroups) : state.openFileIds,
		dirtyFileIds:
			dirtyFileIds.length === state.dirtyFileIds.length ? state.dirtyFileIds : dirtyFileIds,
		activeFileId: activeGroup?.activeFileId ?? null,
		previewFileId: activeGroup?.previewFileId ?? null,
	};
}

const CHAT_LIST_FILTERS_STORAGE_KEY = "izanagi.chatListFilters";

export const DEFAULT_CHAT_MODEL = "sonnet-5-5";
const CHAT_MODEL_STORAGE_KEY = "izanagi.chatModel";
export const DEFAULT_CHAT_EFFORT: ChatEffort = "medium";
const CHAT_EFFORT_STORAGE_KEY = "izanagi.chatEffort";
const CHAT_SUBAGENT_MODELS_STORAGE_KEY = "izanagi.chatSubagentModels";

function readStoredChatModel(): string {
	try {
		const raw = window.localStorage.getItem(CHAT_MODEL_STORAGE_KEY);
		return raw?.trim() || DEFAULT_CHAT_MODEL;
	} catch (err: unknown) {
		console.error("readStoredChatModel failed", err);
		return DEFAULT_CHAT_MODEL;
	}
}

function writeStoredChatModel(model: string): void {
	try {
		window.localStorage.setItem(CHAT_MODEL_STORAGE_KEY, model);
	} catch (err: unknown) {
		console.error("writeStoredChatModel failed", err);
	}
}

function readStoredChatEffort(): ChatEffort {
	try {
		const raw = window.localStorage.getItem(CHAT_EFFORT_STORAGE_KEY);
		return raw !== null && (CHAT_EFFORTS as readonly string[]).includes(raw)
			? (raw as ChatEffort)
			: DEFAULT_CHAT_EFFORT;
	} catch (err: unknown) {
		console.error("readStoredChatEffort failed", err);
		return DEFAULT_CHAT_EFFORT;
	}
}

function writeStoredChatEffort(effort: ChatEffort | null): void {
	try {
		if (effort === null) {
			window.localStorage.removeItem(CHAT_EFFORT_STORAGE_KEY);
			return;
		}
		window.localStorage.setItem(CHAT_EFFORT_STORAGE_KEY, effort);
	} catch (err: unknown) {
		console.error("writeStoredChatEffort failed", err);
	}
}

function readStoredChatSubagentModels(): SubagentModelSelection {
	try {
		const raw = window.localStorage.getItem(CHAT_SUBAGENT_MODELS_STORAGE_KEY);
		if (raw === null) return {};
		const parsed: unknown = JSON.parse(raw);
		if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
		const selection: SubagentModelSelection = {};
		for (const profile of SUBAGENT_PROFILES) {
			const value = (parsed as Record<string, unknown>)[profile];
			if (typeof value === "string" && value.trim().length > 0) selection[profile] = value.trim();
		}
		return selection;
	} catch (err: unknown) {
		console.error("readStoredChatSubagentModels failed", err);
		return {};
	}
}

function writeStoredChatSubagentModels(selection: SubagentModelSelection): void {
	try {
		if (Object.keys(selection).length === 0) {
			window.localStorage.removeItem(CHAT_SUBAGENT_MODELS_STORAGE_KEY);
			return;
		}
		window.localStorage.setItem(CHAT_SUBAGENT_MODELS_STORAGE_KEY, JSON.stringify(selection));
	} catch (err: unknown) {
		console.error("writeStoredChatSubagentModels failed", err);
	}
}

function readStoredChatListFilters(): ChatListFilter[] {
	try {
		return parseStoredChatListFilters(window.localStorage.getItem(CHAT_LIST_FILTERS_STORAGE_KEY));
	} catch (err: unknown) {
		// localStorage が無効化されている／quota 超過。既定値で起動を継続する。
		console.error("readStoredChatListFilters failed", err);
		return parseStoredChatListFilters(null);
	}
}

function writeStoredChatListFilters(filters: readonly ChatListFilter[]): void {
	try {
		window.localStorage.setItem(CHAT_LIST_FILTERS_STORAGE_KEY, JSON.stringify(filters));
	} catch (err: unknown) {
		console.error("writeStoredChatListFilters failed", err);
	}
}

interface PersistedEditorLayout {
	editorGroups: EditorGroup[];
	editorLayout: EditorLayoutNode;
	activeEditorGroupId: string;
}

const EDITOR_LAYOUT_STORAGE_PREFIX = "izanagi.editorLayout.";

function editorLayoutStorageKey(chatId: string): string {
	return `${EDITOR_LAYOUT_STORAGE_PREFIX}${chatId}`;
}

function collectLayoutGroupIds(node: EditorLayoutNode): string[] {
	if (node.kind === "group") return [node.groupId];
	return [...collectLayoutGroupIds(node.first), ...collectLayoutGroupIds(node.second)];
}

/** activeFileId/previewFileId が仕様通り (null か、fileIds に実在する id) かを検証する。 */
function isValidGroupFileRef(value: unknown, fileIds: readonly string[]): boolean {
	return value === null || (typeof value === "string" && fileIds.includes(value));
}

function isEditorGroupShape(value: unknown): value is EditorGroup {
	if (typeof value !== "object" || value === null) return false;
	const group = value as Partial<EditorGroup>;
	if (typeof group.id !== "string" || !Array.isArray(group.fileIds)) return false;
	const fileIds = group.fileIds;
	if (!fileIds.every((id) => typeof id === "string")) return false;
	if (
		typeof group.pinnedCount !== "number" ||
		!Number.isInteger(group.pinnedCount) ||
		group.pinnedCount < 0 ||
		group.pinnedCount > fileIds.length
	) {
		return false;
	}
	return (
		isValidGroupFileRef(group.activeFileId, fileIds) &&
		isValidGroupFileRef(group.previewFileId, fileIds)
	);
}

/** editorLayout ツリーの形とグループ参照 (leaf の groupId が実在するか) を再帰的に検証する。 */
function isEditorLayoutNodeShape(value: unknown, groupIds: ReadonlySet<string>): boolean {
	if (typeof value !== "object" || value === null) return false;
	const node = value as Record<string, unknown>;
	if (node.kind === "group") {
		return typeof node.groupId === "string" && groupIds.has(node.groupId);
	}
	if (node.kind === "split") {
		return (
			(node.direction === "horizontal" || node.direction === "vertical") &&
			isEditorLayoutNodeShape(node.first, groupIds) &&
			isEditorLayoutNodeShape(node.second, groupIds)
		);
	}
	return false;
}

/** localStorage から読み戻した値が壊れたレイアウトを生まないか検証する (グループ参照の整合性など)。 */
function isValidPersistedEditorLayout(value: unknown): value is PersistedEditorLayout {
	if (typeof value !== "object" || value === null) return false;
	const { editorGroups, editorLayout, activeEditorGroupId } = value as Record<string, unknown>;
	if (!Array.isArray(editorGroups) || editorGroups.length === 0) return false;
	if (!editorGroups.every(isEditorGroupShape)) return false;
	const ids = editorGroups.map((group) => group.id);
	const idSet = new Set(ids);
	if (idSet.size !== ids.length) return false;
	if (typeof activeEditorGroupId !== "string" || !idSet.has(activeEditorGroupId)) return false;
	if (!isEditorLayoutNodeShape(editorLayout, idSet)) return false;
	const leafIds = collectLayoutGroupIds(editorLayout as EditorLayoutNode);
	return leafIds.length === idSet.size && leafIds.every((id) => idSet.has(id));
}

function readStoredEditorLayout(chatId: string): PersistedEditorLayout | null {
	try {
		const raw = window.localStorage.getItem(editorLayoutStorageKey(chatId));
		if (raw === null) return null;
		const parsed: unknown = JSON.parse(raw);
		return isValidPersistedEditorLayout(parsed) ? parsed : null;
	} catch (err: unknown) {
		console.error("readStoredEditorLayout failed", err);
		return null;
	}
}

function writeStoredEditorLayout(chatId: string, layout: PersistedEditorLayout): void {
	try {
		window.localStorage.setItem(editorLayoutStorageKey(chatId), JSON.stringify(layout));
	} catch (err: unknown) {
		console.error("writeStoredEditorLayout failed", err);
	}
}

/** チャット削除時に、そのチャットのレイアウト残骸を localStorage から消す。 */
export function removeStoredEditorLayout(chatId: string): void {
	try {
		window.localStorage.removeItem(editorLayoutStorageKey(chatId));
	} catch (err: unknown) {
		console.error("removeStoredEditorLayout failed", err);
	}
}

/**
 * 復元したグループ ID (`editor-<n>`) と新規発行する連番が衝突しないよう、カウンタを追いつかせる。
 * 壊れた/巨大な id で安全整数の範囲を超えないよう Number.isSafeInteger でガードする
 * (超えると `++editorGroupSequence` が no-op 化し、以降のセッション全体で id が衝突し続ける)。
 */
function bumpEditorGroupSequence(ids: readonly string[]): void {
	for (const id of ids) {
		const match = /^editor-(\d+)$/.exec(id);
		if (!match) continue;
		const n = Number(match[1]);
		if (Number.isSafeInteger(n) && n > editorGroupSequence) editorGroupSequence = n;
	}
}

/** 生成中の assistant 応答の暫定状態。カーソル (jobId + attempt + lastSeq) と表示用テキストを持つ。 */
export interface StreamingAssistant {
	jobId: string;
	attempt: number;
	lastSeq: number;
	toolName?: string;
	text: string;
	phase: "thinking" | "advising" | "responding" | "preparingTool";
}

/** 環境ごとに所有されるファイルアップロードの進行状態。 */
export interface EnvironmentUploadOperation {
	id: string;
	environmentId: string;
	progress: number;
}

interface ChatState {
	/** Local DB primary key for the current chat — set immediately on blank chat creation. */
	chatId: string | null;
	/** ID of the environment (sandbox) the current chat belongs to. Keys all file operations. */
	environmentId: string | null;
	/** Display name of the environment the current chat belongs to. */
	environmentName: string | null;
	/** 現在チャットの環境を動かすデバイスの名前。クラウドの環境では null。 */
	environmentRunnerName: string | null;
	/**
	 * 現在チャットの環境コンテナの起動状態。null = 未取得。停止中はファイルツリーを
	 * プレースホルダーに差し替え、コンポーザを無効にする。ドラフト (chatId = null) では追跡しない。
	 */
	environmentStatus: EnvironmentStatus | null;
	/** 現在チャットの環境がサーバーの保守で再起動される期限 (ISO 8601)。予定が無ければ null。 */
	environmentScheduledRestartAt: string | null;
	/**
	 * 取得中に別環境へ切り替わっていた場合は反映しない。再起動の期限を渡さない呼び出し (起動・停止の要求直後) は
	 * 前の値を残す。
	 */
	setEnvironmentStatus: (
		environmentId: string,
		status: EnvironmentStatus,
		scheduledRestartAt?: string | null,
	) => void;
	/** Display name of the project the current chat belongs to. */
	projectName: string | null;
	/** Haiku-generated chat title shown in the ChatHeader. null for a fresh chat before first save. */
	currentTitle: string | null;
	chatCreatedById: string | null;
	chatArchived: boolean;
	/** サーバーが返す現在チャットのツール実行確認モード。 */
	permissionMode: PermissionMode;
	setPermissionMode: (permissionMode: PermissionMode) => void;
	/** ユーザー追記ごとにインクリメントされる楽観的同時実行制御用のリビジョン。送信時に `expectedRevision` として送る。 */
	revision: number;
	messages: ChatMessageDto[];
	/**
	 * アクティブジョブ中に送信されキュー化された「送信待ち」メッセージ。Seq 昇順で保持し、
	 * トランスクリプトとは別エリアに表示する。ジョブ開始時に昇格すると SSE で通常メッセージ
	 * として届き、対応する clientRequestId のエントリはここから取り除かれる。
	 */
	queuedMessages: QueuedChatMessageDto[];
	/**
	 * 楽観的に追加したユーザーメッセージの id。POST 解決時 (`setMessages`) に
	 * クリアされる。描画レイヤーがこの id を「送信中」表示の判定に使う。
	 */
	pendingUserMessageId: string | null;
	status: ConnectionStatus;
	/** 進行中のエージェントジョブ id。停止ボタンの対象。進行中ジョブが無いときは null。 */
	activeAgentJobId: string | null;
	/** When false, the composer is disabled — the user cannot send messages or upload files. */
	chatEnabled: boolean;
	/** True while a chat session's history is being fetched/replayed (switchToChat). */
	isLoadingChat: boolean;
	/** 直近の switchToChat が失敗したか。読み込み失敗を「HITL 解決済み」と誤認しないために使う。 */
	chatLoadFailed: boolean;
	hitlState: HITLState | null;
	/**
	 * 新規チャット下書きで選択中の既存環境 ID。利用できる環境が無い場合は null。
	 * 最初のメッセージ送信時 (CenterPane.handleSend) にチャット作成リクエストへ載せる。
	 */
	newChatEnvironmentId: string | null;
	setNewChatEnvironmentId: (id: string | null) => void;
	/**
	 * 新規チャット下書きの対象エージェント。稼働していなくても保ち、左ペインの絞り込みと見出しに使う。
	 * 送信先の `newChatEnvironmentId` は、このエージェントが稼働中と確かめられたときだけ入る。
	 */
	newChatAgentId: string | null;
	newChatAgentName: string | null;
	/** 下書きの対象エージェントを動かすデバイスの名前。クラウドか、まだ分からなければ null。 */
	newChatAgentRunnerName: string | null;
	setNewChatAgent: (
		agent: { id: string; name: string | null; runner?: { name: string } | null } | null,
	) => void;
	/** 状態再取得が必要なときに新規チャットのエージェント状態を再検証するための世代。 */
	newChatEnvironmentStatusRefreshVersion: number;
	/** 新規チャットの環境状態を確認中か。確認完了までは送信を許可しない。 */
	isNewChatEnvironmentStatusValidating: boolean;
	/** 新規チャットの環境状態確認中フラグを更新する。バックグラウンドポーリングでは変更しない。 */
	setNewChatEnvironmentStatusValidating: (validating: boolean) => void;
	/** 環境状態を再取得し、確認中フラグも立てる。 */
	requestNewChatEnvironmentStatusRefresh: () => void;
	/** Ordered list of chats shown in the left pane. */
	chatList: ChatListEntry[];
	/**
	 * 左ペインの丸で示すチャットの状態 (chatId → 状態)。既読で落ち着いたチャットはキーを持たない。
	 * useChatActivitySync が定期取得して入れる。
	 */
	chatIndicators: Readonly<Record<string, ChatIndicator>>;
	setChatIndicators: (indicators: Readonly<Record<string, ChatIndicator>>) => void;
	/**
	 * チャットの最終メッセージの時刻 (chatId → ISO)。一覧の updatedAt は読み込み時のままなので、
	 * 左ペインの最終更新日時はこちらを優先する。useChatActivitySync が定期取得して入れる。
	 */
	chatLastActivityAt: Readonly<Record<string, string>>;
	setChatLastActivityAt: (lastActivityAt: Readonly<Record<string, string>>) => void;
	/** 左ペインのチャット一覧に掛ける絞り込み (追加順)。ユーザー設定として localStorage に保存する。 */
	chatListFilters: ChatListFilter[];
	setChatListFilters: (filters: ChatListFilter[]) => void;
	/** 送信に使うモデル alias。チャットを切り替えてもユーザー設定として保持する。 */
	selectedModel: string;
	setSelectedModel: (model: string) => void;
	/** 送信に使う推論量。モデルが非対応の場合は null。 */
	selectedEffort: ChatEffort | null;
	setSelectedEffort: (effort: ChatEffort | null) => void;
	/** profile ごとのサブエージェント用モデル alias。キー欠落は親モデルを継承する。 */
	subagentModels: SubagentModelSelection;
	setSubagentModel: (profile: SubagentProfile, modelId: string | null) => void;
	setCurrentTitle: (title: string | null) => void;
	addMessage: (message: ChatMessageDto) => void;
	/**
	 * ストリームから届いたメッセージを冪等に取り込む。楽観追加したユーザーメッセージに
	 * 対応するサーバ版が届いても二重表示しないための詰め替えを行う:
	 * - 同じ id のメッセージが既にあれば何もしない (assistant/monitor や採用済みサーバ行の再配信)。
	 * - `dto.clientRequestId` が非 null で同じ値を持つ既存メッセージがあれば、その要素を
	 *   その場でサーバ DTO に置換する (末尾追加はしない)。`pendingUserMessageId` は変更しない
	 *   (送信の確定 / 取消は `handleSend` 側が担う)。
	 * - どちらでもなければ末尾に追加する。
	 *
	 * いずれの経路でも、到着 dto の `clientRequestId` に一致する `queuedMessages` エントリは
	 * 先に取り除く (昇格行が SSE で届いたら送信待ち表示を消すため)。
	 */
	upsertStreamedMessage: (dto: ChatMessageDto) => void;
	/**
	 * 生成中の assistant 応答。確定メッセージが届くまでの暫定表示に使い、届いた時点で null に戻す。
	 * 進行中ジョブが無いときは null。
	 */
	streamingAssistant: StreamingAssistant | null;
	/**
	 * 生成中断片を取り込む。ジョブが変わったら作り直し、seq が進んでいない断片 (再配信・巻き戻り) は捨てる。
	 * thinkingStarted は「考察中」表示へ、テキストは暫定バブルへ切り替える。
	 */
	applyStreamDelta: (delta: ChatDeltaDto) => void;
	clearStreamingAssistant: () => void;
	/** Replace the entire message list — used after GET/POST /messages returns. Clears the pending-send marker. */
	setMessages: (messages: ChatMessageDto[]) => void;
	/** 後から取得した古い履歴を先頭へ足す。今の先頭より新しい行は既に持っているため捨てる。 */
	prependOlderMessages: (older: ChatMessageDto[]) => void;
	/** 送信待ちメッセージ一覧を差し替える (id で一意化し Seq 昇順に正規化する)。 */
	setQueuedMessages: (messages: QueuedChatMessageDto[]) => void;
	/** 指定 id の送信待ちメッセージを取り除く (個別削除・昇格の反映)。 */
	removeQueuedMessage: (id: string) => void;
	/**
	 * キュー化された送信の POST 応答を反映する。単一の `set` で原子的に:
	 * - messages に同じ `clientRequestId` の行 (楽観行以外) が既にあれば、昇格行が SSE 先行で
	 *   届いているので queuedMessages へは追加せず、楽観行 (`optimisticId`) の除去だけ行う。
	 * - 無ければ楽観行を除去し queuedMessages へ upsert する (POST 先行ケース)。
	 * どちらの場合も `pendingUserMessageId` が `optimisticId` を指していればクリアする。
	 */
	applyQueuedSendResult: (optimisticId: string, queuedMessage: QueuedChatMessageDto) => void;
	/** 指定 id のメッセージを取り除く。送信中マークがその id を指していれば併せて解除する。 */
	removeMessage: (id: string) => void;
	/** Mark / clear the optimistically-added user message awaiting its POST. */
	setPendingUserMessage: (id: string | null) => void;
	setStatus: (status: ConnectionStatus) => void;
	setActiveAgentJob: (jobId: string | null) => void;
	setChatEnabled: (enabled: boolean) => void;
	setLoadingChat: (loading: boolean) => void;
	setChatLoadFailed: (failed: boolean) => void;
	setHITL: (hitl: HITLState) => void;
	clearHITL: () => void;
	updateHITLSegmentation: (boundaries: readonly number[], labels: readonly string[]) => void;
	/**
	 * 読み込み済みディレクトリの直下一覧。キーはワークスペース相対のディレクトリパス
	 * (ルートは空文字列)。ルートすら取得していない (チャット未読込・読込中) 間は null で、
	 * この区別で空表示とスケルトンを出し分ける。フォルダを開いたときにだけキーが増える。
	 */
	environmentFileChildren: ReadonlyMap<string, EnvironmentFileChildrenDto> | null;
	/**
	 * 開いているタブの id → ファイル DTO。ディレクトリを読み込まずに開いたファイル
	 * (Quick Open・URL 復元・エージェントの参照) の解決結果を保持する。
	 */
	openFileEntries: ReadonlyMap<string, EnvironmentFileDto>;
	/**
	 * ワークスペースファイル一覧が更新されるたびに進むカウンタ。一覧そのものを購読せずに
	 * 再同期のきっかけだけが欲しい購読者 (pyright のワークスペース同期) が使う。
	 */
	environmentFilesRevision: number;
	/** 1 ディレクトリ分の直下一覧を反映する。`append` は「さらに表示」で続きを追記する場合。 */
	setEnvironmentDirectoryChildren: (
		dirPath: string,
		children: EnvironmentFileChildrenDto,
		options?: { append?: boolean },
	) => void;
	/** リフレッシュ結果でツリー全体を差し替え、`environmentFilesRevision` を進める。 */
	applyEnvironmentFilesRefresh: (children: ReadonlyMap<string, EnvironmentFileChildrenDto>) => void;
	/** 解決済みのファイル DTO を開きタブの索引へ取り込む (タブは閉じない)。 */
	mergeOpenEnvironmentFileEntries: (files: readonly EnvironmentFileDto[]) => void;
	/**
	 * 照合した開きタブの id 群 (`requestedIds`) のうち、索引に残っていないものを閉じる。
	 * 照合中に開かれたタブを巻き込まないよう、要求に含めた id だけを対象にする。
	 */
	pruneOpenEnvironmentFiles: (
		requestedIds: readonly string[],
		files: readonly EnvironmentFileDto[],
	) => void;
	/** 同一環境のチャット間を切り替えるとき、読み込み済みツリーをそのまま引き継ぐ。 */
	adoptEnvironmentFiles: (
		children: ReadonlyMap<string, EnvironmentFileChildrenDto> | null,
		openFileEntries: ReadonlyMap<string, EnvironmentFileDto>,
	) => void;
	/** 旧環境のファイル一覧・選択・プレビューを破棄し、次の取得までスケルトン状態へ戻す。 */
	clearEnvironmentFiles: () => void;
	/**
	 * ファイルツリーで選択中のノードのパス (ファイル・フォルダ共通)。ハイライト表示に使う。
	 * 何も選択していないときは null。
	 */
	selectedEnvironmentFilesTreeNodePath: string | null;
	selectEnvironmentFilesTreeNode: (path: string | null) => void;
	/** パンくずなど外部操作で表示するツリー上のノード。 */
	environmentFilesTreeNavigation: { path: string | null };
	/** ツリーの祖先を展開して指定ノードを表示・選択する。 */
	navigateToEnvironmentFilesTreeNode: (path: string) => void;
	/** 右パネルにファイルタブとして開いているワークスペースファイルの id (グループ横断、表示順)。 */
	openFileIds: string[];
	/** フォーカス中エディタグループのアクティブなファイル。invariant: null または openFileIds に含まれる。 */
	activeFileId: string | null;
	/**
	 * ファイルを開く操作のたびに増える。既に表示中のファイルを選び直したときも増えるため、
	 * 閉じている右ペインを開くきっかけに使う。
	 */
	fileOpenRequestVersion: number;
	/** フォーカス中エディタグループでプレビュー表示中のファイル。invariant: null または openFileIds に含まれ、dirty にはならない。 */
	previewFileId: string | null;
	/** 分割表示中の各エディタペイン。1 ファイルは 1 グループだけに所属する。 */
	editorGroups: EditorGroup[];
	/** エディタグループの入れ子分割構成。 */
	editorLayout: EditorLayoutNode;
	/** 現在フォーカスを受け取るエディタグループ。 */
	activeEditorGroupId: string;
	/**
	 * ファイルをタブに開いてアクティブ化する。既に (どのグループかを問わず) 開いていればそのグループへ
	 * フォーカスを移してアクティブ化する (重複しない)。プレビュータブだった場合はピン留めに昇格する。
	 */
	openFile: (fileId: string) => void;
	/**
	 * ファイルを開き、指定行をエディタ中央に表示する。`query`/`caseSensitive` を渡すと、
	 * ファイル内の同じ語句全てをハイライトする。既に (どのグループかを問わず) 開いていればそのグループへ
	 * フォーカスを移す (openFile と同じグループ解決)。
	 */
	openFileAtLine: (
		fileId: string,
		line: number,
		spans?: readonly FileRevealSpan[],
		query?: string,
		caseSensitive?: boolean,
	) => void;
	/** ファイルを開き、指定範囲を選択してエディタ中央に表示する (Problems パネルの診断ジャンプ用)。 */
	openFileAtRange: (fileId: string, range: FileRevealRange) => void;
	/**
	 * ファイルエディタへ渡す行ジャンプ要求。fileId をキーにした Map にすることで、
	 * 複数ファイルへの要求が短時間に重なっても互いを上書きしない (単一スロットだと後着の要求が
	 * 先行の要求を消してしまい、片方のジャンプが黙って失われる)。
	 */
	pendingReveals: Record<string, FileRevealRequest>;
	/** 指定ファイルの処理済み行ジャンプ要求を消費する。 */
	consumeReveal: (fileId: string) => void;
	/**
	 * ファイルをプレビュータブとしてフォーカス中グループに開いてアクティブ化する。既存のプレビュータブは
	 * 同じ位置で置き換える。既に (どのグループかを問わず) 開いていればそのグループへフォーカスを移す。
	 */
	openFilePreview: (fileId: string) => void;
	/** ファイルタブを閉じる。アクティブタブだった場合は同グループ内の隣のタブへフォーカスを移す。 */
	closeFile: (fileId: string) => void;
	/**
	 * 指定したファイルと同じグループ内の、指定したファイル以外のファイルタブを閉じる (他グループには
	 * 触れない)。ピン留めされたタブは対象グループ内でも閉じずに残す。
	 */
	closeOtherFiles: (fileId: string) => void;
	/** 指定した方向に空のエディタグループを追加して、そちらへフォーカスを移す。 */
	splitEditor: (direction: EditorSplitDirection) => void;
	/** すべてのファイルタブをフォーカス中グループへ集約し、分割を解除する。 */
	unsplitEditors: () => void;
	/**
	 * localStorage に保存された分割レイアウト (グループ構成・分割ツリー・フォーカス中グループ) を
	 * chatId 単位で復元する。保存が無い、または壊れている場合は何もしない。
	 */
	restoreEditorLayout: (chatId: string) => void;
	/**
	 * ファイルタブをドラッグ&ドロップで別グループへ移す (分割はしない)。index を指定すると移動先
	 * グループ内のその位置へ挿入する (省略時は末尾)。移動先が元と同じグループの場合は index を
	 * 無視し、そのグループへフォーカスを移すだけ (同グループ内での並べ替えは moveOpenFile を使う)。
	 * 未オープンの id / 存在しないグループ id は no-op。
	 */
	moveFileToGroup: (fileId: string, targetGroupId: string, index?: number) => void;
	/**
	 * ファイルタブをドラッグ&ドロップで targetGroupId 側から direction 方向に分割し、新グループへ移す。
	 * targetGroupId が元のグループと同じ場合はそのグループ自身を分割する。
	 */
	splitEditorWithFile: (
		targetGroupId: string,
		direction: EditorSplitDirection,
		fileId: string,
	) => void;
	/** 指定エディタグループへフォーカスを移す。 */
	selectEditorGroup: (groupId: string) => void;
	/**
	 * ファイルタブを同じグループ内の toIndex (移動後の最終 index) へ移動する。ピン群/通常群の境界を
	 * またいだ場合はドロップ位置に応じてピン状態が自動で変わる。未オープンの id は no-op。
	 */
	moveOpenFile: (fileId: string, toIndex: number) => void;
	/** ファイルタブをピン留めする (所属グループのピン群の末尾へ移動)。既にピン留め済み・未オープンは no-op。 */
	pinFile: (fileId: string) => void;
	/** ファイルタブのピン留めを解除する (所属グループの通常群の先頭へ移動)。非ピンタブは no-op。 */
	unpinFile: (fileId: string) => void;
	/** 保存されていない編集がある、開いているファイルタブの id 集合。 */
	dirtyFileIds: string[];
	/** ファイルタブの未保存状態を設定する。 */
	setFileDirty: (fileId: string, dirty: boolean) => void;
	/** プレビュー (transient) タブを常設タブへ昇格させる。プレビュータブでなければ no-op。 */
	promoteFileFromPreview: (fileId: string) => void;
	/**
	 * このチャットのモニター一覧。まだ取得していない間は null、取得済みで無ければ空配列。
	 * environmentFileChildren と同じく null↔空配列でスケルトンと空表示を出し分ける。
	 */
	monitors: ChatMonitor[] | null;
	setMonitors: (monitors: ChatMonitor[]) => void;
	/** このチャットのサブエージェント実行一覧。 */
	subagentRuns: ChatSubagentRun[] | null;
	setSubagentRuns: (runs: ChatSubagentRun[]) => void;
	/**
	 * 1 件の run の状態だけを差し替える。停止ボタンは同じ run に対して複数描かれる (一覧カードと
	 * 詳細ヘッダ) ため、要求の結果を一覧へ書き戻して全インスタンスの表示を揃える。
	 */
	updateSubagentRunStatus: (runId: string, status: SubagentRunStatus) => void;
	selectedSubagentRunId: string | null;
	selectSubagentRun: (runId: string | null) => void;
	/** このチャットのワークフロー実行一覧。宣言フェーズの輪郭とツールカードの状態はここから引く。 */
	workflowRuns: ChatWorkflowRun[];
	setWorkflowRuns: (runs: ChatWorkflowRun[]) => void;
	/** 停止要求の結果を一覧へ書き戻す。次のポーリングを待たずにボタンの表示を揃える。 */
	updateWorkflowRunStatus: (runId: string, status: WorkflowRunStatus) => void;
	/**
	 * 履歴のカード類をユーザーが開閉した状態 (key は ChatView 側で決める)。
	 * 履歴は画面外のブロックを unmount するため、スクロールで開閉が戻らないようここに持つ。
	 */
	disclosureOpen: Record<string, boolean>;
	setDisclosureOpen: (key: string, open: boolean) => void;
	/** 環境ごとのワークスペースアップロード。チャット切替・ファイル一覧破棄後も継続する。 */
	environmentUploadOperations: Record<string, EnvironmentUploadOperation>;
	/** 同じ環境のアップロード所有権を同期的に取得する。取得済みなら null を返す。 */
	beginEnvironmentUpload: (environmentId: string) => string | null;
	/** 所有者が一致するアップロードの進捗だけを更新する。 */
	setEnvironmentUploadProgress: (
		environmentId: string,
		operationId: string,
		percent: number,
	) => void;
	/** 所有者が一致するアップロードだけを終了する。 */
	endEnvironmentUpload: (environmentId: string, operationId: string) => void;
	/** 指定した操作が現在の環境アップロード所有者かを返す。 */
	isEnvironmentUploadOwner: (environmentId: string, operationId: string) => boolean;
	/**
	 * Apply chat metadata (id / title / creator / archived) onto the current
	 * state. `ChatMetadata` は revision を持つため併せて反映する。revision を持たない
	 * `ChatListEntry` の場合は現在の revision を変更しない (後段の loadChat で確定する)。
	 */
	setChatMetadata: (metadata: ChatListEntry | ChatMetadata) => void;
	/** 送信レスポンス等で得た権威的なリビジョンをストアへ反映する。 */
	setChatRevision: (revision: number) => void;
	setChatArchived: (archived: boolean) => void;
	/**
	 * 環境削除後、その環境のチャットを孤児 (アーカイブ済み・環境なし) としてローカル状態へ
	 * 反映する。開いているチャットが対象なら閲覧専用状態も揃える。
	 */
	markEnvironmentDeleted: (environmentId: string) => void;
	/** Chat list management */
	setChatList: (list: ChatListEntry[]) => void;
	addChatListItem: (item: ChatListEntry) => void;
	updateChatListItem: (chatId: string, updates: Partial<ChatListEntry>) => void;
	removeChatListItem: (chatId: string) => void;
	/** Reset chat state (messages, tool calls, status, etc.) but preserve chatList. */
	reset: () => void;
}

/**
 * 断片の種類が示す表示フェーズ。kind が増えたときの対応漏れを型検査で止める
 * (既定へ落ちると 2 つの呼び出し元が別々のフェーズを表示し、原因の分かりにくい不一致になる)。
 */
function phaseForDeltaKind(kind: ChatDeltaKind): StreamingAssistant["phase"] {
	switch (kind) {
		case "text":
			return "responding";
		case "thinkingStarted":
			return "thinking";
		case "toolCallStarted":
			return "preparingTool";
		case "advisorStarted":
			return "advising";
		default:
			// 型検査を通った未知の kind は、まだデプロイしていない backend の新機能。
			// 応答中と誤表示するより、汎用の「考察中」に倒す方が実態に近い。
			kind satisfies never;
			return "thinking";
	}
}

/** 新しいジョブ試行の断片を受け取ったときの初期状態。途中から接続しても届いた分から復元する。 */
function startStreamingAssistant(delta: ChatDeltaDto): StreamingAssistant {
	return {
		jobId: delta.jobId,
		attempt: delta.attempt,
		lastSeq: delta.seq,
		text: delta.kind === "text" ? (delta.text ?? "") : "",
		phase: phaseForDeltaKind(delta.kind),
		toolName: delta.kind === "toolCallStarted" ? (delta.toolName ?? undefined) : undefined,
	};
}

const INITIAL_CHAT_STATE = {
	chatId: null as string | null,
	environmentId: null as string | null,
	environmentName: null as string | null,
	environmentRunnerName: null as string | null,
	environmentStatus: null as EnvironmentStatus | null,
	environmentScheduledRestartAt: null as string | null,
	projectName: null as string | null,
	currentTitle: null as string | null,
	chatCreatedById: null as string | null,
	chatArchived: false,
	permissionMode: "acceptEdits" as PermissionMode,
	revision: 0,
	messages: [] as ChatMessageDto[],
	streamingAssistant: null as StreamingAssistant | null,
	queuedMessages: [] as QueuedChatMessageDto[],
	pendingUserMessageId: null as string | null,
	status: "idle" as ConnectionStatus,
	activeAgentJobId: null as string | null,
	chatEnabled: true,
	isLoadingChat: false,
	chatLoadFailed: false,
	hitlState: null as HITLState | null,
	newChatEnvironmentId: null as string | null,
	newChatAgentId: null as string | null,
	newChatAgentName: null as string | null,
	newChatAgentRunnerName: null as string | null,
	newChatEnvironmentStatusRefreshVersion: 0,
	isNewChatEnvironmentStatusValidating: false,
	environmentFileChildren: null as ReadonlyMap<string, EnvironmentFileChildrenDto> | null,
	openFileEntries: new Map<string, EnvironmentFileDto>() as ReadonlyMap<string, EnvironmentFileDto>,
	environmentFilesRevision: 0,
	selectedEnvironmentFilesTreeNodePath: null as string | null,
	environmentFilesTreeNavigation: { path: null },
	openFileIds: [] as string[],
	activeFileId: null as string | null,
	fileOpenRequestVersion: 0,
	pendingReveals: {} as Record<string, FileRevealRequest>,
	previewFileId: null as string | null,
	editorGroups: initialEditorGroups(),
	editorLayout: initialEditorLayout(),
	activeEditorGroupId: INITIAL_EDITOR_GROUP_ID,
	dirtyFileIds: [] as string[],
	monitors: null as ChatMonitor[] | null,
	subagentRuns: null as ChatSubagentRun[] | null,
	selectedSubagentRunId: null as string | null,
	workflowRuns: [] as ChatWorkflowRun[],
	disclosureOpen: {} as Record<string, boolean>,
	environmentUploadOperations: {} as Record<string, EnvironmentUploadOperation>,
};

/** ファイルを開く操作の更新に、開く要求の回数 (fileOpenRequestVersion) の加算を添える。 */
function setOpeningFile(
	set: StoreApi<ChatState>["setState"],
	update: (state: ChatState) => Partial<ChatState>,
): void {
	set((state) => ({
		...update(state),
		fileOpenRequestVersion: state.fileOpenRequestVersion + 1,
	}));
}

export const useChatStore = create<ChatState>()((set, get) => ({
	...INITIAL_CHAT_STATE,
	chatList: [] as ChatListEntry[],
	chatIndicators: {} as Readonly<Record<string, ChatIndicator>>,
	// 5秒ごとのポーリングが同じ内容を返す間は参照を保ち、左ペインの無駄な再描画を避ける。
	setChatIndicators: (chatIndicators) =>
		set((state) => {
			const previous = state.chatIndicators;
			const unchanged =
				Object.keys(previous).length === Object.keys(chatIndicators).length &&
				Object.entries(chatIndicators).every(([chatId, dot]) => previous[chatId] === dot);
			return unchanged ? {} : { chatIndicators };
		}),
	chatLastActivityAt: {} as Readonly<Record<string, string>>,
	setChatLastActivityAt: (chatLastActivityAt) =>
		set((state) =>
			shallow(state.chatLastActivityAt, chatLastActivityAt) ? {} : { chatLastActivityAt },
		),
	chatListFilters: readStoredChatListFilters(),
	setChatListFilters: (filters: ChatListFilter[]) => {
		writeStoredChatListFilters(filters);
		set({ chatListFilters: filters });
	},
	selectedModel: readStoredChatModel(),
	setSelectedModel: (selectedModel) => {
		writeStoredChatModel(selectedModel);
		set({ selectedModel });
	},
	selectedEffort: readStoredChatEffort(),
	setSelectedEffort: (selectedEffort) => {
		writeStoredChatEffort(selectedEffort);
		set({ selectedEffort });
	},
	subagentModels: readStoredChatSubagentModels(),
	setSubagentModel: (profile, modelId) =>
		set((state) => {
			const subagentModels = { ...state.subagentModels };
			if (modelId === null) delete subagentModels[profile];
			else subagentModels[profile] = modelId;
			writeStoredChatSubagentModels(subagentModels);
			return { subagentModels };
		}),
	setCurrentTitle: (currentTitle) => set({ currentTitle }),
	addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
	upsertStreamedMessage: (dto) =>
		set((state) => {
			// 昇格行が SSE で届いたら送信待ち表示を消すため、全経路で clientRequestId 一致の
			// キューエントリを先に取り除く。一致が無ければ参照を維持して再描画を避ける。
			const queuedMessages =
				dto.clientRequestId != null &&
				state.queuedMessages.some((q) => q.clientRequestId === dto.clientRequestId)
					? state.queuedMessages.filter((q) => q.clientRequestId !== dto.clientRequestId)
					: state.queuedMessages;
			if (state.messages.some((m) => m.id === dto.id)) {
				return queuedMessages === state.queuedMessages ? state : { queuedMessages };
			}
			const matchIndex =
				dto.clientRequestId != null
					? state.messages.findIndex((m) => m.clientRequestId === dto.clientRequestId)
					: -1;
			if (matchIndex === -1) {
				return { messages: [...state.messages, dto], queuedMessages };
			}
			const messages = state.messages.map((m, i) => (i === matchIndex ? dto : m));
			return { messages, queuedMessages };
		}),
	applyStreamDelta: (delta) =>
		set((state) => {
			const current = state.streamingAssistant;
			// 再試行は連番を 0 から振り直すため、試行が変わったら古い本文ごと作り直す。
			if (current === null || current.jobId !== delta.jobId || current.attempt !== delta.attempt) {
				return { streamingAssistant: startStreamingAssistant(delta) };
			}
			if (delta.seq <= current.lastSeq) return state;
			return {
				streamingAssistant: {
					...current,
					lastSeq: delta.seq,
					// 本文を運ぶのは text 断片だけ。フェーズ切り替えの合図では積み上げた本文を保つ。
					text: delta.kind === "text" ? current.text + (delta.text ?? "") : current.text,
					phase: phaseForDeltaKind(delta.kind),
					toolName: delta.kind === "toolCallStarted" ? (delta.toolName ?? undefined) : undefined,
				},
			};
		}),
	clearStreamingAssistant: () =>
		set((state) => (state.streamingAssistant === null ? state : { streamingAssistant: null })),
	setMessages: (messages) => set({ messages, pendingUserMessageId: null }),
	prependOlderMessages: (older) =>
		set((state) => {
			const oldestOrder = state.messages[0]?.order ?? Number.POSITIVE_INFINITY;
			return { messages: [...older.filter((m) => m.order < oldestOrder), ...state.messages] };
		}),
	setQueuedMessages: (messages) => set({ queuedMessages: normalizeQueued(messages) }),
	removeQueuedMessage: (id) =>
		set((state) => ({ queuedMessages: state.queuedMessages.filter((q) => q.id !== id) })),
	applyQueuedSendResult: (optimisticId, queuedMessage) =>
		set((state) => {
			const alreadyStreamed = state.messages.some(
				(m) =>
					m.id !== optimisticId &&
					m.clientRequestId != null &&
					m.clientRequestId === queuedMessage.clientRequestId,
			);
			const messages = state.messages.filter((m) => m.id !== optimisticId);
			const pendingUserMessageId =
				state.pendingUserMessageId === optimisticId ? null : state.pendingUserMessageId;
			if (alreadyStreamed) {
				return { messages, pendingUserMessageId };
			}
			return {
				messages,
				pendingUserMessageId,
				queuedMessages: normalizeQueued([...state.queuedMessages, queuedMessage]),
			};
		}),
	removeMessage: (id) =>
		set((state) => ({
			messages: state.messages.filter((m) => m.id !== id),
			pendingUserMessageId: state.pendingUserMessageId === id ? null : state.pendingUserMessageId,
		})),
	setPendingUserMessage: (pendingUserMessageId) => set({ pendingUserMessageId }),
	setStatus: (status) => set({ status }),
	setActiveAgentJob: (activeAgentJobId) => set({ activeAgentJobId }),
	setChatEnabled: (enabled) => set({ chatEnabled: enabled }),
	setLoadingChat: (isLoadingChat) => set({ isLoadingChat }),
	setChatLoadFailed: (chatLoadFailed) => set({ chatLoadFailed }),
	setHITL: (hitlState) => set({ hitlState }),
	clearHITL: () => set({ hitlState: null }),
	setNewChatEnvironmentId: (newChatEnvironmentId) =>
		set((state) => {
			if (state.newChatEnvironmentId === newChatEnvironmentId) {
				return { newChatEnvironmentId };
			}
			if (state.chatId !== null) return { newChatEnvironmentId };
			return {
				newChatEnvironmentId,
				environmentFileChildren: null,
				openFileEntries: new Map<string, EnvironmentFileDto>(),
				...clearedEditorState(),
				selectedEnvironmentFilesTreeNodePath: null,
			};
		}),
	setNewChatAgent: (agent) =>
		set({
			newChatAgentId: agent?.id ?? null,
			newChatAgentName: agent?.name ?? null,
			newChatAgentRunnerName: agent?.runner?.name ?? null,
		}),
	setNewChatEnvironmentStatusValidating: (isNewChatEnvironmentStatusValidating) =>
		set({ isNewChatEnvironmentStatusValidating }),
	requestNewChatEnvironmentStatusRefresh: () =>
		set((state) => ({
			newChatEnvironmentStatusRefreshVersion: state.newChatEnvironmentStatusRefreshVersion + 1,
			isNewChatEnvironmentStatusValidating: true,
		})),
	updateHITLSegmentation: (boundaries, labels) =>
		set((state) =>
			state.hitlState?.kind === "segmentation"
				? {
						hitlState: {
							...state.hitlState,
							boundaries,
							labels,
						},
					}
				: state,
		),
	setEnvironmentDirectoryChildren: (dirPath, children, options) =>
		set((state) => {
			const next = new Map(state.environmentFileChildren ?? []);
			const previous = options?.append ? next.get(dirPath) : undefined;
			next.set(
				dirPath,
				previous
					? { entries: [...previous.entries, ...children.entries], hasMore: children.hasMore }
					: children,
			);
			return { environmentFileChildren: next };
		}),
	applyEnvironmentFilesRefresh: (children) =>
		set((state) => ({
			environmentFileChildren: children,
			environmentFilesRevision: state.environmentFilesRevision + 1,
			// 選択中ノードの親ディレクトリを読み込み済みで、そこから消えていれば選択を解除する。
			// 親が未読込のときは消えたと断定できないため選択を維持する。
			selectedEnvironmentFilesTreeNodePath: selectedNodeAfterRefresh(
				children,
				state.selectedEnvironmentFilesTreeNodePath,
			),
		})),
	mergeOpenEnvironmentFileEntries: (files) =>
		set((state) => {
			if (files.length === 0) return state;
			const next = new Map(state.openFileEntries);
			for (const file of files) next.set(file.id, file);
			return { openFileEntries: next };
		}),
	pruneOpenEnvironmentFiles: (requestedIds, files) =>
		set((state) => {
			const found = new Map(files.map((file) => [file.id, file]));
			const requested = new Set(requestedIds);
			const tabs = closedMissingTabState(state, (id) => !requested.has(id) || found.has(id));
			// 閉じたタブの解決結果は不要になるため、開いたままの id の分だけ作り直す。
			const openFileEntries = new Map<string, EnvironmentFileDto>();
			for (const id of tabs.openFileIds) {
				const file = found.get(id) ?? state.openFileEntries.get(id);
				if (file) openFileEntries.set(id, file);
			}
			return { openFileEntries, ...tabs };
		}),
	adoptEnvironmentFiles: (environmentFileChildren, openFileEntries) =>
		set({ environmentFileChildren, openFileEntries }),
	clearEnvironmentFiles: () =>
		set((state) => ({
			environmentFileChildren: null,
			openFileEntries: new Map<string, EnvironmentFileDto>(),
			environmentFilesRevision: state.environmentFilesRevision + 1,
			...clearedEditorState(),
			selectedEnvironmentFilesTreeNodePath: null,
		})),
	selectEnvironmentFilesTreeNode: (path) => set({ selectedEnvironmentFilesTreeNodePath: path }),
	navigateToEnvironmentFilesTreeNode: (path) =>
		set({
			selectedEnvironmentFilesTreeNodePath: path,
			environmentFilesTreeNavigation: { path },
		}),
	openFile: (fileId) =>
		setOpeningFile(set, (state) => {
			const existingGroup = findGroupWithFile(state.editorGroups, fileId);
			if (
				existingGroup &&
				state.activeFileId === fileId &&
				existingGroup.previewFileId !== fileId
			) {
				return state;
			}
			return resolveOpenFileGroups(state.editorGroups, state.activeEditorGroupId, fileId);
		}),
	openFilePreview: (fileId) =>
		setOpeningFile(set, (state) => {
			const existingGroup = findGroupWithFile(state.editorGroups, fileId);
			if (existingGroup) {
				if (state.activeFileId === fileId) return state;
				const editorGroups = state.editorGroups.map((group) =>
					group.id === existingGroup.id ? { ...group, activeFileId: fileId } : group,
				);
				return {
					editorGroups,
					activeEditorGroupId: existingGroup.id,
					activeFileId: fileId,
					previewFileId: existingGroup.previewFileId,
				};
			}
			const activeGroup = activeEditorGroup(state.editorGroups, state.activeEditorGroupId);
			const replaceIndex =
				activeGroup?.previewFileId != null
					? activeGroup.fileIds.indexOf(activeGroup.previewFileId)
					: -1;
			const editorGroups = state.editorGroups.map((group) => {
				if (group.id !== state.activeEditorGroupId) return group;
				const fileIds =
					replaceIndex === -1
						? [...group.fileIds, fileId]
						: group.fileIds.map((id, index) => (index === replaceIndex ? fileId : id));
				return { ...group, fileIds, activeFileId: fileId, previewFileId: fileId };
			});
			return {
				editorGroups,
				openFileIds: allOpenFileIds(editorGroups),
				activeFileId: fileId,
				previewFileId: fileId,
			};
		}),
	openFileAtLine: (fileId, line, spans, query, caseSensitive) =>
		setOpeningFile(set, (state) => {
			const validSpans = spans
				?.filter(
					(span) =>
						Number.isInteger(span.start) && Number.isInteger(span.end) && span.end > span.start,
				)
				.map((span) => ({ start: span.start, end: span.end }));
			// set() のたびにこの fileId のエントリだけ新しいオブジェクト参照にすることで、
			// FileEditor 側の useEffect が同じ行への再要求でも参照の変化を検知して再実行される
			// (他ファイルのエントリは参照を維持するので、無関係なタブの再レンダーは起きない)。
			const pendingReveals = {
				...state.pendingReveals,
				[fileId]: {
					line: Math.max(1, line),
					...(validSpans?.length ? { spans: validSpans } : {}),
					...(query ? { query, caseSensitive: caseSensitive ?? false } : {}),
				},
			};
			return {
				...resolveOpenFileGroups(state.editorGroups, state.activeEditorGroupId, fileId),
				pendingReveals,
			};
		}),
	openFileAtRange: (fileId, range) =>
		setOpeningFile(set, (state) => ({
			...resolveOpenFileGroups(state.editorGroups, state.activeEditorGroupId, fileId),
			// openFileAtLine と同じく、この fileId のエントリだけ新しい参照にして FileEditor 側の
			// useEffect を確実に再実行させる (対象が既にアクティブなタブでも消費されるようにする)。
			pendingReveals: {
				...state.pendingReveals,
				[fileId]: { line: Math.max(1, range.startLineNumber), range },
			},
		})),
	consumeReveal: (fileId) =>
		set((state) => {
			if (!(fileId in state.pendingReveals)) return state;
			const pendingReveals = { ...state.pendingReveals };
			delete pendingReveals[fileId];
			return { pendingReveals };
		}),
	closeFile: (fileId) => set((state) => closedTabState(state, fileId)),
	closeOtherFiles: (fileId) =>
		set((state) => {
			const targetGroup = findGroupWithFile(state.editorGroups, fileId);
			if (!targetGroup) return state;
			// 他グループには触れない。対象グループ内でも、ピン留めタブは閉じずに残す。
			const keepInTarget = new Set(
				targetGroup.fileIds.filter((_, index) => index < targetGroup.pinnedCount),
			);
			keepInTarget.add(fileId);
			const previewFileId = targetGroup.previewFileId === fileId ? fileId : null;
			const editorGroups = state.editorGroups.map((group) =>
				group.id === targetGroup.id
					? {
							...group,
							fileIds: group.fileIds.filter((id) => keepInTarget.has(id)),
							activeFileId: fileId,
							previewFileId,
						}
					: group,
			);
			const openFileIds = allOpenFileIds(editorGroups);
			return {
				editorGroups,
				openFileIds,
				dirtyFileIds: state.dirtyFileIds.filter((id) => openFileIds.includes(id)),
				activeFileId: fileId,
				previewFileId,
				activeEditorGroupId: targetGroup.id,
			};
		}),
	splitEditor: (direction) =>
		set((state) => {
			if (state.editorGroups.length >= 4) return state;
			const activeGroup = activeEditorGroup(state.editorGroups, state.activeEditorGroupId);
			if (!activeGroup) return state;
			const newGroupId = `editor-${++editorGroupSequence}`;
			return {
				editorGroups: [
					...state.editorGroups,
					{ id: newGroupId, fileIds: [], pinnedCount: 0, activeFileId: null, previewFileId: null },
				],
				editorLayout: splitEditorLayout(
					state.editorLayout,
					state.activeEditorGroupId,
					newGroupId,
					direction,
				),
				activeEditorGroupId: newGroupId,
				activeFileId: null,
				previewFileId: null,
			};
		}),
	unsplitEditors: () =>
		set((state) => {
			if (state.editorGroups.length === 1) return state;
			const activeGroup = activeEditorGroup(state.editorGroups, state.activeEditorGroupId);
			if (!activeGroup) return state;
			// 各グループのピン留めタブを先頭へ集約し、それ以外は元の順序のまま後ろへ続ける。
			const pinned = state.editorGroups.flatMap((group) =>
				group.fileIds.slice(0, group.pinnedCount),
			);
			const unpinned = state.editorGroups.flatMap((group) =>
				group.fileIds.slice(group.pinnedCount),
			);
			const fileIds = [...pinned, ...unpinned];
			const pinnedCount = pinned.length;
			const activeFileId = activeGroup.activeFileId ?? fileIds[0] ?? null;
			const previewFileId = activeGroup.previewFileId;
			return {
				editorGroups: [{ ...activeGroup, fileIds, pinnedCount, activeFileId, previewFileId }],
				editorLayout: { kind: "group", groupId: activeGroup.id },
				openFileIds: fileIds,
				activeFileId,
				previewFileId,
			};
		}),
	restoreEditorLayout: (chatId) =>
		set((state) => {
			const persisted = readStoredEditorLayout(chatId);
			if (!persisted) return state;
			bumpEditorGroupSequence(persisted.editorGroups.map((group) => group.id));
			// セグメンテーションは下部ペインへ移したため、旧レイアウトのタブは読み戻さない。
			const editorGroups = persisted.editorGroups.map(withoutLegacyPanelTabs);
			const openFileIds = allOpenFileIds(editorGroups);
			const activeGroup = activeEditorGroup(editorGroups, persisted.activeEditorGroupId);
			return {
				editorGroups,
				editorLayout: persisted.editorLayout,
				activeEditorGroupId: persisted.activeEditorGroupId,
				openFileIds,
				activeFileId: activeGroup?.activeFileId ?? null,
				previewFileId: activeGroup?.previewFileId ?? null,
			};
		}),
	moveFileToGroup: (fileId, targetGroupId, index) =>
		set((state) => {
			const sourceGroup = findGroupWithFile(state.editorGroups, fileId);
			if (!sourceGroup) return state;
			if (!state.editorGroups.some((group) => group.id === targetGroupId)) return state;
			if (sourceGroup.id === targetGroupId) {
				if (
					index === undefined &&
					sourceGroup.activeFileId === fileId &&
					state.activeEditorGroupId === targetGroupId
				) {
					return state;
				}
				const editorGroups = state.editorGroups.map((group) =>
					group.id === targetGroupId ? { ...group, activeFileId: fileId } : group,
				);
				return {
					editorGroups,
					activeEditorGroupId: targetGroupId,
					activeFileId: fileId,
					previewFileId: sourceGroup.previewFileId,
				};
			}
			const { updatedSourceGroup, emptiedGroupId } = detachFileFromGroup(sourceGroup, fileId);
			const movedGroups = state.editorGroups.map((group) => {
				if (group.id === sourceGroup.id) return updatedSourceGroup;
				if (group.id === targetGroupId) {
					const clamped = Math.max(
						0,
						Math.min(index ?? group.fileIds.length, group.fileIds.length),
					);
					// 移動元でのピン状態は引き継がない (Blocking #2 の既存合意通り)。挿入位置が
					// ピン群の範囲内ならピン群の一員として扱い、pinnedCount をその分増やす。
					// 増やさないと、後続のピン済みタブが境界の外へ押し出されて非ピン扱いになってしまう。
					const pinnedCount = group.pinnedCount + (clamped < group.pinnedCount ? 1 : 0);
					return {
						...group,
						fileIds: insertAt(group.fileIds, fileId, clamped),
						pinnedCount,
						activeFileId: fileId,
					};
				}
				return group;
			});
			const { editorGroups, editorLayout } = closeIfEmptied(
				movedGroups,
				state.editorLayout,
				emptiedGroupId,
			);
			return {
				editorGroups,
				editorLayout,
				openFileIds: allOpenFileIds(editorGroups),
				activeEditorGroupId: targetGroupId,
				activeFileId: fileId,
				previewFileId: null,
			};
		}),
	splitEditorWithFile: (targetGroupId, direction, fileId) =>
		set((state) => {
			if (state.editorGroups.length >= 4) return state;
			if (!state.editorGroups.some((group) => group.id === targetGroupId)) return state;
			const sourceGroup = findGroupWithFile(state.editorGroups, fileId);
			if (!sourceGroup) return state;
			const { updatedSourceGroup, emptiedGroupId } = detachFileFromGroup(sourceGroup, fileId);
			const newGroupId = `editor-${++editorGroupSequence}`;
			const splitGroups = [
				...state.editorGroups.map((group) =>
					group.id === sourceGroup.id ? updatedSourceGroup : group,
				),
				{
					id: newGroupId,
					fileIds: [fileId],
					pinnedCount: 0,
					activeFileId: fileId,
					previewFileId: null,
				},
			];
			// targetGroupId は分割前のグループ集合を参照するため、emptiedGroupId の除去より先に分割する
			// (自グループを自身の端へ分割する自己分割ケースでも targetGroupId を解決できるようにする)。
			const splitLayout = splitEditorLayout(
				state.editorLayout,
				targetGroupId,
				newGroupId,
				direction,
			);
			const { editorGroups, editorLayout } = closeIfEmptied(
				splitGroups,
				splitLayout,
				emptiedGroupId,
			);
			return {
				editorGroups,
				openFileIds: allOpenFileIds(editorGroups),
				editorLayout,
				activeEditorGroupId: newGroupId,
				activeFileId: fileId,
				previewFileId: null,
			};
		}),
	selectEditorGroup: (groupId) =>
		set((state) => {
			if (state.activeEditorGroupId === groupId) return state;
			const group = state.editorGroups.find((candidate) => candidate.id === groupId);
			if (!group) return state;
			return {
				activeEditorGroupId: groupId,
				activeFileId: group.activeFileId,
				previewFileId: group.previewFileId,
			};
		}),
	moveOpenFile: (fileId, toIndex) =>
		set((state) => {
			const group = findGroupWithFile(state.editorGroups, fileId);
			if (!group) return state;
			const from = group.fileIds.indexOf(fileId);
			const wasPinned = from < group.pinnedCount;
			const without = group.fileIds.filter((id) => id !== fileId);
			const pinnedWithout = wasPinned ? group.pinnedCount - 1 : group.pinnedCount;
			const target = Math.max(0, Math.min(toIndex, without.length));
			if (target === from) return state;
			// 境界ちょうど (target === pinnedWithout) は元のピン状態を維持する。
			const nowPinned = target < pinnedWithout || (target === pinnedWithout && wasPinned);
			const fileIds = insertAt(without, fileId, target);
			const pinnedCount = pinnedWithout + (nowPinned ? 1 : 0);
			const editorGroups = state.editorGroups.map((g) =>
				g.id === group.id ? { ...g, fileIds, pinnedCount } : g,
			);
			return { editorGroups, openFileIds: allOpenFileIds(editorGroups) };
		}),
	pinFile: (fileId) =>
		set((state) => {
			const group = findGroupWithFile(state.editorGroups, fileId);
			if (!group) return state;
			if (group.fileIds.indexOf(fileId) < group.pinnedCount) return state;
			const editorGroups = setFilePinned(state.editorGroups, group, fileId, true);
			return { editorGroups, openFileIds: allOpenFileIds(editorGroups) };
		}),
	unpinFile: (fileId) =>
		set((state) => {
			const group = findGroupWithFile(state.editorGroups, fileId);
			if (!group) return state;
			if (group.fileIds.indexOf(fileId) >= group.pinnedCount) return state;
			const editorGroups = setFilePinned(state.editorGroups, group, fileId, false);
			return { editorGroups, openFileIds: allOpenFileIds(editorGroups) };
		}),
	setFileDirty: (fileId, dirty) =>
		set((state) => {
			const has = state.dirtyFileIds.includes(fileId);
			if (dirty === has) return state;
			const dirtyFileIds = dirty
				? [...state.dirtyFileIds, fileId]
				: state.dirtyFileIds.filter((id) => id !== fileId);
			if (!dirty) return { dirtyFileIds };
			const promoted = promotedFromPreview(
				state.editorGroups,
				state.activeEditorGroupId,
				state.previewFileId,
				fileId,
			);
			return promoted ? { dirtyFileIds, ...promoted } : { dirtyFileIds };
		}),
	promoteFileFromPreview: (fileId) =>
		set(
			(state) =>
				promotedFromPreview(
					state.editorGroups,
					state.activeEditorGroupId,
					state.previewFileId,
					fileId,
				) ?? state,
		),
	setMonitors: (monitors) =>
		set((state) => ({ monitors: reuseUnchanged(state.monitors, monitors) })),
	setEnvironmentStatus: (environmentId, environmentStatus, scheduledRestartAt) =>
		set((state) =>
			state.chatId !== null && state.environmentId === environmentId
				? {
						environmentStatus,
						environmentScheduledRestartAt:
							scheduledRestartAt === undefined
								? state.environmentScheduledRestartAt
								: scheduledRestartAt,
					}
				: state,
		),
	setSubagentRuns: (subagentRuns) =>
		set((state) => ({ subagentRuns: reuseUnchanged(state.subagentRuns, subagentRuns) })),
	updateSubagentRunStatus: (runId, status) =>
		set((state) => ({
			subagentRuns:
				state.subagentRuns?.map((run) => (run.id === runId ? { ...run, status } : run)) ?? null,
		})),
	selectSubagentRun: (selectedSubagentRunId) => set({ selectedSubagentRunId }),
	setWorkflowRuns: (workflowRuns) => set({ workflowRuns }),
	updateWorkflowRunStatus: (runId, status) =>
		set((state) => ({
			workflowRuns: state.workflowRuns.map((run) => (run.id === runId ? { ...run, status } : run)),
		})),
	setDisclosureOpen: (key, open) =>
		set((state) => ({ disclosureOpen: { ...state.disclosureOpen, [key]: open } })),
	beginEnvironmentUpload: (environmentId) => {
		let operationId: string | null = null;
		set((state) => {
			if (state.environmentUploadOperations[environmentId] !== undefined) return state;
			const nextOperationId = newRequestId();
			operationId = nextOperationId;
			return {
				environmentUploadOperations: {
					...state.environmentUploadOperations,
					[environmentId]: { id: nextOperationId, environmentId, progress: 0 },
				},
			};
		});
		return operationId;
	},
	setEnvironmentUploadProgress: (environmentId, operationId, percent) =>
		set((state) => {
			const operation = state.environmentUploadOperations[environmentId];
			if (operation?.id !== operationId || operation.progress === percent) return state;
			return {
				environmentUploadOperations: {
					...state.environmentUploadOperations,
					[environmentId]: { ...operation, progress: percent },
				},
			};
		}),
	endEnvironmentUpload: (environmentId, operationId) =>
		set((state) => {
			if (state.environmentUploadOperations[environmentId]?.id !== operationId) return state;
			const environmentUploadOperations = { ...state.environmentUploadOperations };
			delete environmentUploadOperations[environmentId];
			return { environmentUploadOperations };
		}),
	isEnvironmentUploadOwner: (environmentId, operationId) =>
		get().environmentUploadOperations[environmentId]?.id === operationId,
	setChatMetadata: (metadata) =>
		set(() => ({
			chatId: metadata.id,
			environmentId: metadata.environmentId,
			environmentName: metadata.environmentName,
			environmentRunnerName:
				"environmentRunnerName" in metadata ? (metadata.environmentRunnerName ?? null) : null,
			projectName: metadata.projectName,
			currentTitle: metadata.title ?? null,
			chatCreatedById: metadata.createdById,
			chatArchived: metadata.archived,
			...("permissionMode" in metadata
				? { permissionMode: metadata.permissionMode ?? "acceptEdits" }
				: {}),
			...("revision" in metadata ? { revision: metadata.revision } : {}),
		})),
	setPermissionMode: (permissionMode) => set({ permissionMode }),
	setChatRevision: (revision) => set({ revision }),
	setChatArchived: (chatArchived) => set({ chatArchived }),
	markEnvironmentDeleted: (environmentId) =>
		set((state) => ({
			chatList: state.chatList.map((c) =>
				c.environmentId === environmentId
					? { ...c, archived: true, environmentId: null, environmentName: null }
					: c,
			),
			...(state.chatId !== null && state.environmentId === environmentId
				? {
						chatArchived: true,
						environmentId: null,
						environmentName: null,
						environmentRunnerName: null,
					}
				: {}),
		})),
	setChatList: (chatList) => set({ chatList }),
	addChatListItem: (item) => set((state) => ({ chatList: [item, ...state.chatList] })),
	updateChatListItem: (chatId, updates) =>
		set((state) => ({
			chatList: state.chatList.map((c) => (c.id === chatId ? { ...c, ...updates } : c)),
		})),
	removeChatListItem: (chatId) =>
		set((state) => ({
			chatList: state.chatList.filter((c) => c.id !== chatId),
		})),
	reset: () =>
		set((state) => ({
			...INITIAL_CHAT_STATE,
			selectedModel: state.selectedModel,
			selectedEffort: state.selectedEffort,
			subagentModels: state.subagentModels,
			environmentUploadOperations: state.environmentUploadOperations,
			// 送信先が外れるので、表示中の下書きに状態を確かめ直させる。
			newChatEnvironmentStatusRefreshVersion: state.newChatEnvironmentStatusRefreshVersion + 1,
		})),
}));

// 分割レイアウトの変更を chatId 単位で localStorage へ書き戻す。reset 直後の初期状態への
// 巻き戻しや、restoreEditorLayout 前後の一時的な chatId のみの変化では書き込まない
// (値そのものが変わっていなければ何もしない)。
useChatStore.subscribe((state, prevState) => {
	if (state.chatId === null) return;
	if (
		state.editorGroups === prevState.editorGroups &&
		state.editorLayout === prevState.editorLayout &&
		state.activeEditorGroupId === prevState.activeEditorGroupId
	) {
		return;
	}
	writeStoredEditorLayout(state.chatId, {
		editorGroups: state.editorGroups,
		editorLayout: state.editorLayout,
		activeEditorGroupId: state.activeEditorGroupId,
	});
});

/**
 * 現在のコンテキストで有効な環境 ID。新規チャット下書き中は選択中の新規環境 ID を、
 * 既存チャットを開いているときはそのチャットの環境 ID を返す。
 */
export function selectEffectiveEnvironmentId(state: ChatState): string | null {
	return state.chatId === null ? state.newChatEnvironmentId : state.environmentId;
}

/**
 * `selectEffectiveEnvironmentId` の環境を動かすデバイスの名前。クラウドの環境か、下書きでまだ分からなければ null。
 */
export function selectEffectiveEnvironmentRunnerName(state: ChatState): string | null {
	if (state.chatId !== null) return state.environmentRunnerName;
	return state.newChatEnvironmentId === state.newChatAgentId ? state.newChatAgentRunnerName : null;
}

/**
 * 今のエージェント (開いているチャットのエージェント、新規チャット下書きなら対象のエージェント) の ID。
 * 下書きの対象が稼働していなくても返すので、左ペインの絞り込みに使える。
 */
export function selectCurrentAgentId(state: ChatState): string | null {
	return state.chatId === null ? state.newChatAgentId : state.environmentId;
}

/** `selectCurrentAgentId` のエージェント名。 */
export function selectCurrentAgentName(state: ChatState): string | null {
	return state.chatId === null ? state.newChatAgentName : state.environmentName;
}

/** 指定環境のアップロード操作。未実行なら null。 */
export function selectEnvironmentUploadOperation(
	state: ChatState,
	environmentId: string | null,
): EnvironmentUploadOperation | null {
	return environmentId === null ? null : (state.environmentUploadOperations[environmentId] ?? null);
}

/** 指定環境のアップロード進捗。未実行なら null。 */
export function selectEnvironmentUploadProgress(
	state: ChatState,
	environmentId: string | null,
): number | null {
	return selectEnvironmentUploadOperation(state, environmentId)?.progress ?? null;
}

/** 最新の assistant 応答が報告したコンテキスト使用量。 */
export function selectLatestContextUsage(state: ChatState): ContextUsage | null {
	for (let index = state.messages.length - 1; index >= 0; index--) {
		const message = state.messages[index];
		if (message.role === "assistant" && message.contextUsage) {
			return message.contextUsage;
		}
	}
	return null;
}

/**
 * compaction の要約生成中か。直近のメッセージが進行中マーカーかどうかで導出するため、
 * 後続メッセージ (要約行・失敗通知・assistant 応答) の到着で自然に false へ戻る。
 */
export function selectIsCompacting(state: ChatState): boolean {
	const last = state.messages.at(-1);
	return last?.role === "systemNotice" && isCompactingNotice(last.stopDetails);
}

/**
 * メッセージ送信・添付・下書きへのパス挿入が可能か。
 * 入力欄でのタイピングはこの条件に関わらず常に可能 (ComposerInput の sendDisabled)。
 * 「メッセージにパスを追加」のようなメニューの表示可否にも使う。
 * streaming 中はフォローアップとしてキュー送信できる。stopping 中は 409 になるため不可。
 */
export function selectCanComposeMessage(s: ChatState): boolean {
	return (
		s.status !== "stopping" &&
		s.hitlState === null &&
		s.chatEnabled &&
		!s.isLoadingChat &&
		!s.chatArchived &&
		// 環境が削除された孤児チャットは閲覧専用 (サーバー側も 409 で拒否する)。
		(s.chatId === null || s.environmentId !== null) &&
		!selectIsChatEnvironmentStopped(s)
	);
}

/**
 * 現在チャットの環境が起動していない (停止中・起動準備中・終了処理中・異常) と分かっているか。
 * 状態未取得 (null) は起動中とみなし、サーバー側の 409 に委ねる。ドラフトは対象外
 * (選択肢が稼働中の環境に限られる)。
 */
export function selectIsChatEnvironmentStopped(s: ChatState): boolean {
	return (
		s.chatId !== null &&
		s.environmentId !== null &&
		s.environmentStatus !== null &&
		s.environmentStatus !== "running"
	);
}

/** 確定済みチャットでのみ、ファイルパスをメッセージへ挿入できることを返す。 */
export function selectCanInsertPathIntoMessage(s: ChatState): boolean {
	return s.chatId !== null && selectCanComposeMessage(s);
}

/**
 * ワークスペースへファイルを取り込める状態かを返す。環境が確定して起動しており、ファイルツリーの
 * 直下の取得も済んでいることを要求する。ファイルツリーの作成・アップロード・OS ドロップで
 * 同じ条件を使う。
 */
export function selectIsWorkspaceUploadReady(s: ChatState): boolean {
	return (
		selectEffectiveEnvironmentId(s) !== null &&
		s.environmentFileChildren !== null &&
		!selectIsChatEnvironmentStopped(s)
	);
}

/**
 * 新規チャットの送信準備が完了しているかを返す。既存チャットはドラフト環境の状態確認や
 * ファイル一覧取得の影響を受けず、環境選択・ファイル取得・状態確認をすべて満たした場合だけ
 * 新規チャットを送信可能とする。
 */
export function selectIsDraftEnvironmentReady(s: ChatState): boolean {
	return (
		s.chatId !== null ||
		(s.newChatEnvironmentId !== null &&
			s.environmentFileChildren !== null &&
			!s.isNewChatEnvironmentStatusValidating)
	);
}
